import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setToken(response.data.access_token);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Giriş başarısız';
      return { success: false, error: message };
    }
  };

  const register = async (email, name, password, username = null, phone = null) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, name, password, username, phone });
      // New register returns success with verification message
      return { 
        success: true, 
        requiresVerification: response.data.requires_verification,
        message: response.data.message
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'Kayıt başarısız';
      return { success: false, error: message };
    }
  };

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { token: verificationToken });
      if (response.data.access_token) {
        setToken(response.data.access_token);
        setUser(response.data.user);
      }
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Doğrulama başarısız';
      return { success: false, error: message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'İşlem başarısız';
      return { success: false, error: message };
    }
  };

  const resetPassword = async (resetToken, password) => {
    try {
      const response = await axios.post(`${API}/auth/reset-password`, { token: resetToken, password });
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Şifre sıfırlama başarısız';
      return { success: false, error: message };
    }
  };

  // Google OAuth - Emergent Auth
  const loginWithGoogle = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google/session?session_id=${sessionId}`);
      setToken(response.data.access_token);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Google giriş başarısız';
      return { success: false, error: message };
    }
  };

  // Redirect to Google OAuth
  const initiateGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Legacy social login - shows info message
  const loginWithSocial = async (provider) => {
    if (provider === 'google') {
      initiateGoogleLogin();
      return { success: true, redirecting: true };
    }
    
    try {
      const response = await axios.post(`${API}/auth/social/${provider}`);
      if (response.data.success === false) {
        return { success: false, error: response.data.message };
      }
      setToken(response.data.access_token);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || `${provider} girişi başarısız`;
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const updateFavorites = (favorites) => {
    if (user) {
      setUser({ ...user, favorites });
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      token, 
      loading, 
      login, 
      register,
      verifyEmail,
      forgotPassword,
      resetPassword,
      loginWithSocial,
      loginWithGoogle,
      initiateGoogleLogin,
      logout,
      updateFavorites,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
